/**
 * World-space surface pattern overlay — TSL node builders.
 *
 * Produces a color node that blends a configurable world-space surface pattern
 * over a base color. Three pattern types are supported:
 *
 * - **uniform**: regular square grid lines, with optional per-axis control.
 * - **brick**: staggered brick-bond (2:1 width-to-height) with configurable
 *   grout color and joint thickness.
 * - **tile**: square tile grid with configurable grout color and joint thickness.
 *
 * Two projection modes are provided for all pattern types:
 *
 * 1. **Tangent** — builds an orthonormal basis (T, B) in the surface plane
 *    using world-up as the reference direction, then samples the pattern on
 *    `dot(positionWorld, T) / cellSize` and `dot(positionWorld, B) /
 *    cellSize`. Cells are **exactly** cellSize meters along any flat surface
 *    regardless of orientation. On smoothly curved geometry the per-fragment
 *    normal variation causes the tangent basis to rotate, so the pattern
 *    bends locally; near the world ±Y poles a fallback from world-up to
 *    world-X is used to avoid a degenerate cross product.
 *
 * 2. **Triplanar** — samples the pattern on each of the three world-axis
 *    planes (YZ, XZ, XY) and blends by the absolute world normal. Pattern
 *    is distributed symmetrically on curved surfaces with no pole
 *    singularities, but diagonal flat faces show two blended projections
 *    rather than a single clean one.
 *
 * Both modes use `fwidth`-based anti-aliasing so lines stay crisp at any
 * camera distance without moiré.
 */

import {
  abs,
  clamp,
  cross,
  dFdx,
  dFdy,
  dot,
  float,
  floor,
  fract,
  fwidth,
  max,
  min,
  mix,
  normalize,
  normalLocal,
  normalWorld,
  positionView,
  positionWorld,
  sign,
  smoothstep,
  step,
  transformNormalToView,
  vec2,
  vec3,
} from "three/tsl";
import type { ObjectKind } from "../modeling/types.ts";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type GridPattern = "uniform" | "brick" | "tile";
export type GridProjection = "tangent" | "triplanar";
export type GridLines = "both" | "h" | "v";

// ---------------------------------------------------------------------------
// Per-primitive default projection
// ---------------------------------------------------------------------------

/**
 * Pick the pattern projection mode that best fits a primitive's inherent
 * geometry when no explicit `gridProjection` is set on its material.
 *
 * Curved primitives (sphere, cylinder, cone, torus, circle, curve) default
 * to `triplanar` because the tangent-space projection visibly bends and
 * pinches near the poles of smoothly varying normals. Everything else —
 * cubes, planes, empties, props, imported geometry, mannequins — defaults
 * to `tangent`, which gives exact cells on flat surfaces at any orientation.
 *
 * The material's explicit `gridProjection` always overrides this hint;
 * this function only provides the fallback when the field is undefined.
 */
export function getDefaultGridProjection(
  kind: ObjectKind,
): "tangent" | "triplanar" {
  switch (kind) {
    case "sphere":
    case "icosphere":
    case "cylinder":
    case "cone":
    case "torus":
    case "circle":
    case "curve":
      return "triplanar";
    default:
      return "tangent";
  }
}

// TSL functions are generically typed, but chained intermediate values
// frequently collapse to Node<"float"> via the wrong overload. We use `any`
// for these intermediates so vector component access (.x/.y/.z) type-checks;
// runtime values are real TSL nodes and behave correctly regardless.
type N = any;

// ---------------------------------------------------------------------------
// Private: brick/tile constants
// ---------------------------------------------------------------------------

/** Brick width-to-height ratio. 1 cell = brick height; width = BRICK_RATIO cells. */
const BRICK_RATIO = 2.0;

// ---------------------------------------------------------------------------
// Private: coordinate rotation (resolved at shader-graph compile time)
// ---------------------------------------------------------------------------

/**
 * Rotate a 2D coordinate by a fixed angle. The angle is a JS-time constant so
 * the trig values are baked directly into the shader graph — no GPU trig, no
 * runtime branch.
 *
 * Standard 2D rotation: u' = u·cos − v·sin, v' = u·sin + v·cos.
 * With 0° the function is a no-op (returns `coord` unchanged).
 */
function rotateCoord(coord: N, angleRad: number): N {
  if (angleRad === 0) return coord;
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return vec2(
    coord.x.mul(c).sub(coord.y.mul(s)),
    coord.x.mul(s).add(coord.y.mul(c)),
  );
}

// ---------------------------------------------------------------------------
// Private: 2D pattern mask helpers
// ---------------------------------------------------------------------------

/**
 * Thin grid line mask with screen-space derivative anti-aliasing.
 * `coord` is in cell units (1 unit = 1 cell). Returns [0, 1] where 1 = on a
 * grid line. The `lines` parameter controls which axis directions to render:
 * "both" (default), "h" (horizontal seams only), or "v" (vertical seams only).
 *
 * Each branch compiles to a distinct shader graph at JS time — no GPU
 * branching overhead.
 */
function uniformMask(coord: N, lines: GridLines): N {
  const f: N = abs(fract(coord.sub(0.5)).sub(0.5));

  if (lines === "h") {
    // Horizontal seams only — lines at integer V values (f.y near 0)
    const dv: N = fwidth(coord.y);
    return clamp(
      float(1.0).sub(smoothstep(float(0.0), dv.mul(1.5), f.y)),
      0,
      1,
    );
  }

  if (lines === "v") {
    // Vertical seams only — lines at integer U values (f.x near 0)
    const du: N = fwidth(coord.x);
    return clamp(
      float(1.0).sub(smoothstep(float(0.0), du.mul(1.5), f.x)),
      0,
      1,
    );
  }

  // Both axes — the original grid2D logic
  const d: N = fwidth(coord);
  return clamp(
    float(1.0).sub(smoothstep(float(0.0), d.mul(1.5), min(f.x, f.y))),
    0,
    1,
  );
}

/**
 * Grid line mask with a physically-sized grout joint.
 * `coord` is in cell units. `groutFrac` is the half-thickness of each joint
 * as a fraction of the cell dimension [0, 0.5) — the same fraction is applied
 * in both U and V so the joint appears at the same visual proportion of each
 * brick/tile dimension.
 */
function groutMask(coord: N, groutFrac: N): N {
  const f: N = abs(fract(coord.sub(0.5)).sub(0.5));
  const d: N = fwidth(coord);
  // Soft inner edge avoids a hard staircase when groutFrac << d.
  const inner: N = max(groutFrac.sub(d.mul(1.0)), float(0.0));
  return clamp(
    float(1.0).sub(smoothstep(inner, groutFrac.add(d.mul(1.0)), min(f.x, f.y))),
    0,
    1,
  );
}

/**
 * Brick-bond pattern mask. `coord` is in brick-height cell units.
 * Brick width = BRICK_RATIO height units. Alternate courses are offset by
 * half a brick width (= BRICK_RATIO/2 height units) to produce the
 * running-bond stagger.
 */
function brickMask(coord: N, groutFrac: N): N {
  // Integer course index in the height direction
  const course: N = floor(coord.y);
  // Convert U to brick-unit space and stagger alternate courses by 0.5 brick units.
  // fract(course * 0.5) alternates: 0 for even courses, 0.5 for odd courses.
  const uBrick: N = coord.x.div(BRICK_RATIO).add(fract(course.mul(0.5)));
  return groutMask(vec2(uBrick, coord.y), groutFrac);
}

/**
 * Square tile pattern mask. Identical to a symmetric grout grid.
 */
function tileMask(coord: N, groutFrac: N): N {
  return groutMask(coord, groutFrac);
}

// ---------------------------------------------------------------------------
// Private: projection — surface-tangent 2D coordinate
// ---------------------------------------------------------------------------

/**
 * Project world position onto the surface-tangent (T, B) orthonormal frame
 * and return the 2D coordinate in cell units (divided by cellSize).
 *
 * T = cross(N, ref) gives the horizontal direction on the surface relative
 * to world-up (gravity). B = N × T gives the perpendicular direction. On a
 * vertical wall, T points horizontally and B points upward (world-Y),
 * matching the intuitive "horizontal / vertical" decomposition.
 */
function tangentCoord(cellSize: N): N {
  const n: N = normalize(normalWorld);

  // Reference up-axis for tangent basis orientation. Using world-up keeps
  // grid lines aligned with gravity where possible. When the normal is
  // (nearly) parallel to world-up, fall back to world-X to avoid a
  // degenerate cross product. The switch is discrete at |n.y| > 0.99;
  // the ~8° band around each pole is where surface orientation is
  // inherently ambiguous anyway.
  const worldUp: N = vec3(0, 1, 0);
  const worldX: N = vec3(1, 0, 0);
  const atPole: N = step(float(0.99), abs(n.y));
  const ref: N = mix(worldUp, worldX, atPole);

  // Orthonormal tangent frame (T, B) in the surface plane.
  const t: N = normalize(cross(n, ref));
  const b: N = cross(n, t);

  // Project world position onto (T, B) and divide by cellSize.
  const u: N = dot(positionWorld, t).div(cellSize);
  const v: N = dot(positionWorld, b).div(cellSize);
  return vec2(u, v);
}

// ---------------------------------------------------------------------------
// Private: dispatch pattern mask for a given 2D coordinate
// ---------------------------------------------------------------------------

function samplePattern(
  pattern: GridPattern,
  coord: N,
  lines: GridLines,
  groutFrac: N,
): N {
  switch (pattern) {
    case "brick":
      return brickMask(coord, groutFrac);
    case "tile":
      return tileMask(coord, groutFrac);
    default:
      return uniformMask(coord, lines);
  }
}

// ---------------------------------------------------------------------------
// Public: single entry-point overlay builder
// ---------------------------------------------------------------------------

/**
 * Build a base-color node that overlays the requested surface pattern.
 *
 * @param pattern   - Which pattern to render (`uniform`, `brick`, or `tile`).
 * @param projection - `tangent` (surface-tangent frame) or `triplanar`
 *                    (world-axis blend — best for curved geometry).
 * @param baseColor - The underlying surface color node.
 * @param cellSize  - Cell/unit size in world meters (as a TSL uniform).
 * @param color     - Pattern line/grout color (as a TSL uniform).
 * @param opacity   - Pattern opacity [0–1] (as a TSL uniform).
 * @param groutFrac - Grout joint half-thickness as a fraction of cellSize
 *                    (as a TSL uniform). Used only by `brick` and `tile`.
 * @param lines     - Which grid axes to show (`uniform` only): `"both"`,
 *                    `"h"` (horizontal), or `"v"` (vertical). Resolved at
 *                    shader-graph compile time — no GPU branch overhead.
 * @param rotation  - Rotation in radians, applied to the 2D pattern
 *                    coordinate before sampling. JS-time constant — trig
 *                    values baked into the shader, zero GPU overhead.
 */
export function buildPatternOverlayColorNode(
  pattern: GridPattern,
  projection: GridProjection,
  baseColor: N,
  cellSize: N,
  color: N,
  opacity: N,
  groutFrac: N,
  lines: GridLines,
  rotation: number,
): N {
  let mask: N;

  if (projection === "tangent") {
    const coord: N = rotateCoord(tangentCoord(cellSize), rotation);
    mask = samplePattern(pattern, coord, lines, groutFrac);
  } else {
    // Triplanar: sharpen the absolute normal so flat faces get clean
    // single-axis projections; only 45°-ish surfaces blend two planes.
    const nAbs: N = abs(normalWorld);
    const w: N = (nAbs as N).pow(float(4));
    const wSum: N = max(w.x.add(w.y).add(w.z), float(0.0001));
    const wN: N = w.div(wSum);

    // World position in cell units, with per-plane rotation applied.
    const p: N = (positionWorld as N).div(cellSize);

    const gYZ: N = samplePattern(
      pattern,
      rotateCoord(p.yz, rotation),
      lines,
      groutFrac,
    );
    const gXZ: N = samplePattern(
      pattern,
      rotateCoord(p.xz, rotation),
      lines,
      groutFrac,
    );
    const gXY: N = samplePattern(
      pattern,
      rotateCoord(p.xy, rotation),
      lines,
      groutFrac,
    );

    mask = clamp(
      gYZ.mul(wN.x).add(gXZ.mul(wN.y)).add(gXY.mul(wN.z)),
      float(0),
      float(1),
    );
  }

  return mix(baseColor, vec3(color), mask.mul(opacity));
}

// ---------------------------------------------------------------------------
// Bump / normal perturbation — brick and tile only
// ---------------------------------------------------------------------------

/**
 * Shared chamfer height for a 2D grid coordinate.
 *
 * - `f` is distance from the nearest cell boundary, in [0, 0.5].
 * - Returns 0 inside the grout channel (f < groutFrac), 1 on the flat top,
 *   and a smooth chamfered bevel in between.
 *
 * Bevel width = max(groutFrac * 2, 2 pixels) so the gradient is always
 * shallow enough to avoid over-bright specular highlights at low bumpScale,
 * and the transition is always at least 2 pixels wide for clean AA.
 * The smoothstep lower edge is pulled back by 1 pixel so the grout-to-bevel
 * boundary itself is anti-aliased rather than a hard step.
 */
function chamferHeight(fMin: N, groutFrac: N): N {
  const px: N = fwidth(fMin);
  const bevelEnd: N = groutFrac.add(max(groutFrac.mul(2.0), px.mul(2.0)));
  return smoothstep(groutFrac.sub(px), bevelEnd, fMin);
}

/** Brick-bond bump height. Applies the running-bond stagger before chamfering. */
function brickBumpHeight(coord: N, groutFrac: N): N {
  const course: N = floor(coord.y);
  const uBrick: N = coord.x.div(BRICK_RATIO).add(fract(course.mul(0.5)));
  const f: N = abs(fract(vec2(uBrick, coord.y).sub(0.5)).sub(0.5));
  return chamferHeight(min(f.x, f.y), groutFrac);
}

/** Square tile bump height. */
function tileBumpHeight(coord: N, groutFrac: N): N {
  const f: N = abs(fract(coord.sub(0.5)).sub(0.5));
  return chamferHeight(min(f.x, f.y), groutFrac);
}

function samplePatternHeight(pattern: GridPattern, coord: N, groutFrac: N): N {
  switch (pattern) {
    case "brick":
      return brickBumpHeight(coord, groutFrac);
    case "tile":
      return tileBumpHeight(coord, groutFrac);
    default:
      return float(1);
  }
}

/**
 * Perturb the view-space surface normal from a scalar height field using
 * the screen-space cotangent-frame formula (the same approach Three.js uses
 * for its built-in BumpMap node).
 *
 * Works correctly for any UV parameterization because only the final scalar
 * height value is needed — no explicit tangent/bitangent vectors required.
 *
 * @param heightNode - Scalar height field [0, 1] for the current fragment.
 * @param bumpScale  - Perturbation intensity (a TSL uniform). Typical range 0–2.
 * @returns Perturbed view-space normal suitable for assignment to `normalNode`.
 */
function bumpFromHeight(heightNode: N, bumpScale: N): N {
  // Screen-space gradients of view-space position and height.
  const dP1: N = dFdx(positionView);
  const dP2: N = dFdy(positionView);
  const dH1: N = dFdx(heightNode);
  const dH2: N = dFdy(heightNode);

  // Cotangent frame: R1 and R2 span the tangent plane relative to view normal.
  //
  // We cannot use `normalView` here: when this node is assigned to
  // `material.normalNode`, Three.js TSL resolves `normalView` to the
  // normalNode output itself — a circular dependency that produces NaN.
  // `transformNormalToView(normalLocal)` reads the interpolated vertex normal
  // attribute directly, which is never affected by normalNode.
  const N_: N = normalize(transformNormalToView(normalLocal) as N);
  const R1: N = cross(dP2, N_);
  const R2: N = cross(N_, dP1);
  const det: N = dot(dP1, R1); // scalar triple product (signed volume)

  // Height-field gradient projected onto the surface plane.
  const vGrad: N = (sign(det) as N).mul(
    (dH1 as N).mul(R1).add((dH2 as N).mul(R2)),
  );

  // Perturb the normal; clamp det away from zero before dividing to guard
  // against NaN on degenerate fragments (silhouette edges, back-faces).
  const safeDet: N = max(abs(det), float(1e-5));
  return normalize((safeDet as N).mul(N_).sub(vGrad.mul(bumpScale)));
}

/**
 * Build a normal node that adds relief to `brick` or `tile` patterns.
 *
 * Computes a smooth height field from the grout layout and perturbs the
 * view-space surface normal using screen-space derivatives, giving the
 * appearance of raised tile/brick surfaces with chamfered grout edges.
 * The output is in view space and suitable for direct assignment to
 * `material.normalNode`.
 *
 * @param pattern    - Must be `"brick"` or `"tile"` (uniform has no relief).
 * @param projection - `tangent` or `triplanar`, matching the color overlay.
 * @param cellSize   - Cell/unit size uniform matching the color overlay.
 * @param groutFrac  - Grout half-thickness uniform matching the color overlay.
 * @param bumpScale  - Relief intensity uniform [0–0.1].
 * @param rotation   - Rotation in radians, matching the color overlay.
 */
export function buildPatternBumpNormalNode(
  pattern: GridPattern,
  projection: GridProjection,
  cellSize: N,
  groutFrac: N,
  bumpScale: N,
  rotation: number,
): N {
  let height: N;

  if (projection === "tangent") {
    const coord: N = rotateCoord(tangentCoord(cellSize), rotation);
    height = samplePatternHeight(pattern, coord, groutFrac);
  } else {
    // Triplanar: blend height samples from the three axis-aligned planes.
    const nAbs: N = abs(normalWorld);
    const w: N = (nAbs as N).pow(float(4));
    const wSum: N = max(w.x.add(w.y).add(w.z), float(0.0001));
    const wN: N = w.div(wSum);
    const p: N = (positionWorld as N).div(cellSize);

    const hYZ: N = samplePatternHeight(
      pattern,
      rotateCoord(p.yz, rotation),
      groutFrac,
    );
    const hXZ: N = samplePatternHeight(
      pattern,
      rotateCoord(p.xz, rotation),
      groutFrac,
    );
    const hXY: N = samplePatternHeight(
      pattern,
      rotateCoord(p.xy, rotation),
      groutFrac,
    );

    height = hYZ.mul(wN.x).add(hXZ.mul(wN.y)).add(hXY.mul(wN.z));
  }

  return bumpFromHeight(height, bumpScale);
}
